import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Lock, Loader2, ArrowLeft, AlertTriangle } from "lucide-react";
import { StarsBackground, DISCORD_URL } from "@/components/StarsBackground";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/hooks/useAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ORDER = ["ar", "smg", "lmg", "shotgun", "sniper", "marksman", "pistol"];
const CATEGORY_LABELS = {
  ar: "Assault Rifles",
  smg: "SMGs",
  lmg: "LMGs",
  shotgun: "Shotguns",
  sniper: "Snipers",
  marksman: "Marksman",
  pistol: "Pistols",
};

export default function ValueDetail() {
  const { pkgId } = useParams();
  const { me, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | locked | error

  useEffect(() => {
    if (authLoading) return;
    if (!me) {
      setState("error");
      return;
    }
    axios
      .get(`${API}/me/values/${pkgId}`)
      .then((r) => {
        setData(r.data);
        setState("ok");
      })
      .catch((err) => {
        if (err?.response?.status === 423) setState("locked");
        else if (err?.response?.status === 403) setState("error");
        else setState("error");
      });
  }, [pkgId, me, authLoading]);

  if (authLoading || state === "loading") {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        <StarsBackground />
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (state === "locked") {
    return (
      <Shell title="Locked">
        <div className="rv-card rounded-xl p-10 text-center">
          <AlertTriangle className="mx-auto" size={42} style={{ color: "var(--rv-gold)" }} />
          <h1 className="font-display font-black uppercase mt-5 text-3xl md:text-4xl">Locked from a new network</h1>
          <p className="text-white/70 mt-4 max-w-xl mx-auto">
            This pack was unlocked from a different network location. If you're trying to access it from somewhere new, DM Rumple on Discord to verify.
          </p>
          <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="btn-primary mt-7 justify-center">
            Open Discord
          </a>
        </div>
      </Shell>
    );
  }

  if (state === "error" || !data) {
    return (
      <Shell title="No access">
        <div className="rv-card rounded-xl p-10 text-center">
          <Lock className="mx-auto" size={36} style={{ color: "var(--rv-red)" }} />
          <h1 className="font-display font-black uppercase mt-5 text-3xl">No access</h1>
          <p className="text-white/70 mt-3">You don't own this pack yet.</p>
          <Link to="/values" className="btn-primary mt-6 justify-center">
            <ArrowLeft size={14} /> Back to My Values
          </Link>
        </div>
      </Shell>
    );
  }

  const { package: pkg, content } = data;
  const guns = (content.guns || []).filter((g) => g.enabled);
  const grouped = guns.reduce((acc, g) => {
    const k = g.category || "ar";
    (acc[k] = acc[k] || []).push(g);
    return acc;
  }, {});

  return (
    <Shell title={pkg.name}>
      <div
        className="rv-protected"
        onContextMenu={(e) => e.preventDefault()}
        onCopy={(e) => { e.preventDefault(); try { e.clipboardData.setData("text/plain", ""); } catch (err) { /* clipboard blocked in some browsers */ } }}
        onCut={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      >
      <Link to="/values" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/55 hover:text-white">
        <ArrowLeft size={12} /> Back to My Values
      </Link>
      <h1 className="font-display font-black uppercase mt-4 text-4xl md:text-5xl lg:text-6xl leading-tight">
        {pkg.name}
      </h1>
      <p className="text-white/65 mt-3 text-base md:text-lg">{pkg.tagline}</p>

      {content.intro && (
        <div className="rv-card rounded-xl p-6 mt-10 text-white/85 whitespace-pre-wrap">{content.intro}</div>
      )}

      {content.video_url && (
        <div className="mt-8 rv-card rounded-xl overflow-hidden">
          {/youtube\.com|youtu\.be/.test(content.video_url) ? (
            <iframe
              src={content.video_url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/")}
              title="Setup video"
              className="w-full aspect-video"
              allowFullScreen
            />
          ) : (
            <video src={content.video_url} controls className="w-full aspect-video bg-black" />
          )}
        </div>
      )}

      <div className="mt-12 space-y-10">
        {CATEGORY_ORDER.filter((c) => grouped[c]?.length).map((cat) => (
          <section key={cat}>
            <div className="section-label">{CATEGORY_LABELS[cat]}</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {grouped[cat].map((g, i) => (
                <div key={`${cat}-${g.name || i}`} className="rv-card rounded-xl p-5">
                  <div className="font-display uppercase tracking-tight text-xl">{g.name}</div>
                  {cat === "ar" ? (
                    <div className="mt-4 grid grid-cols-4 gap-2">
                      {["1x", "2x", "3x", "4x"].map((s) => (
                        <div key={s} className="text-center">
                          <div className="text-[11px] uppercase tracking-widest text-white/55 font-semibold">{s.toUpperCase()}</div>
                          <div className="mt-1.5 rounded bg-black/40 border border-white/10 py-2 px-1">
                            <div className="text-[10px] uppercase tracking-widest text-white/55">V</div>
                            <div className="font-mono font-bold text-white text-lg leading-tight">{g[`v_${s}`] || "—"}</div>
                          </div>
                          <div className="mt-1.5 rounded bg-black/40 border border-white/10 py-2 px-1">
                            <div className="text-[10px] uppercase tracking-widest text-white/55">H</div>
                            <div className="font-mono font-bold text-white text-lg leading-tight">{g[`h_${s}`] || "—"}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-4 grid grid-cols-2 gap-3 text-center">
                      <div className="rounded bg-black/40 border border-white/10 py-3 px-3">
                        <div className="text-[11px] uppercase tracking-widest text-white/55 font-semibold">Vertical</div>
                        <div className="font-mono font-bold text-white text-3xl mt-1.5">{g.v_value || "—"}</div>
                      </div>
                      <div className="rounded bg-black/40 border border-white/10 py-3 px-3">
                        <div className="text-[11px] uppercase tracking-widest text-white/55 font-semibold">Horizontal</div>
                        <div className="font-mono font-bold text-white text-3xl mt-1.5">{g.h_value || g.value || "—"}</div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
        {guns.length === 0 && (
          <div className="rv-card rounded-xl p-10 text-center text-white/55">
            Rumple hasn't added gun values to this pack yet. Check back soon or ping him on Discord.
          </div>
        )}
      </div>

      {content.notes && (
        <div className="rv-card rounded-xl p-6 mt-10 text-white/80 whitespace-pre-wrap">{content.notes}</div>
      )}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="relative min-h-screen" data-testid="value-detail-page">
      <StarsBackground />
      <Header />
      <main className="relative z-10 pt-32 md:pt-40 pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">{children}</div>
      </main>
      <Footer />
    </div>
  );
}
